import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth.js'

export const routes = [
  { path: '/login', name: 'login', component: () => import('@/views/LoginView.vue'), meta: { public: true } },
  { path: '/signup', name: 'signup', component: () => import('@/views/SignupView.vue'), meta: { public: true } },
  // Public, and deliberately NOT redirected away for a signed-in visitor the way /login
  // and /signup are: a person who is signed in on this device may still be following a
  // reset link because someone else is signed in on another one.
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/ForgotPasswordView.vue'),
    meta: { public: true, allowAuthenticated: true },
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/views/ResetPasswordView.vue'),
    meta: { public: true, allowAuthenticated: true },
  },
  { path: '/', name: 'home', component: () => import('@/views/HomeView.vue') },
  // /goals used to render HomeView again -- the same screen under a second path, which is
  // what the middle nav tab pointed at. It is the categories manager now.
  { path: '/categories', name: 'categories', component: () => import('@/views/CategoriesView.vue') },
  { path: '/goals/new', name: 'goal-new', component: () => import('@/views/GoalWizardView.vue') },
  { path: '/goals/:id', name: 'goal-detail', component: () => import('@/views/GoalDetailView.vue') },
  { path: '/goals/:id/edit', name: 'goal-edit', component: () => import('@/views/GoalEditView.vue') },
  { path: '/profile', name: 'profile', component: () => import('@/views/ProfileView.vue') },
  { path: '/:pathMatch(.*)*', redirect: '/' },
]

// A reset link is a one-time credential with an hour to live, and it is opened on whatever
// device the email happened to be read on -- routinely one whose installed shell predates
// this feature. That old shell has no /reset-password route, so its catch-all sends the
// visitor to `/` and the guard below then sends them to /login, carrying the token along
// as a query parameter of `next`. The link looks broken; the person has one hour and no
// idea what went wrong. (The service-worker denylist in vite.config.js prevents this for
// shells built after it, but cannot help one already installed -- which is precisely the
// case that matters.)
//
// So: wherever a `token` surfaces, on any route that is not already the reset screen, take
// it there. Reading it out of `next` as well as the query is what recovers the exact shape
// observed live: /login?next=/?token=...
//
// The token is not trusted here and does not need to be — it is a lookup key the server
// verifies (and refuses, for anything expired, used, or invented). Nothing is decided by
// its value on this side.
export function resetTokenFrom(query) {
  const direct = query?.token
  if (typeof direct === 'string' && direct.length > 0) return direct

  const next = query?.next
  if (typeof next !== 'string') return null
  // `next` is a path with its own query string, not a URL — parse only its query part, and
  // never let it decide anything but the token.
  const separator = next.indexOf('?')
  if (separator === -1) return null
  const nested = new URLSearchParams(next.slice(separator + 1)).get('token')
  return nested && nested.length > 0 ? nested : null
}

// These guards are a UX affordance only — they steer navigation for a signed-in-looking
// browser tab. The server is the real security boundary and re-checks every request;
// removing or bypassing this guard client-side cannot grant access to anything.
export function installGuards(router) {
  router.beforeEach(async (to) => {
    // Before anything else, including the auth check: a reset link must reach its screen
    // whether or not somebody is signed in on this device.
    if (to.name !== 'reset-password') {
      const token = resetTokenFrom(to.query)
      if (token) return { name: 'reset-password', query: { token } }
    }

    const auth = useAuthStore()
    if (auth.status === 'unknown') await auth.fetchMe()

    if (!to.meta.public && !auth.isAuthenticated) {
      return { name: 'login', query: to.fullPath === '/' ? undefined : { next: to.fullPath } }
    }
    if (to.meta.public && auth.isAuthenticated && !to.meta.allowAuthenticated) {
      return { name: 'home' }
    }
    return true
  })
}

export function createAppRouter() {
  const router = createRouter({ history: createWebHistory(), routes })
  installGuards(router)
  return router
}
